/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: GPT-5 Codex
 🏢 Company: Ring2All LLC
 📄 File: api/cdrFilterApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API client functions for CDR Filters module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  CdrFilter,
  CreateCdrFilterPayload,
  UpdateCdrFilterPayload,
  ListCdrFiltersParams,
  ListCdrFiltersResponse,
  GetCdrFilterResponse,
} from '../types';

const CDR_FILTERS_ENDPOINT = '/api/telephony/cdr-filters';

/**
 * Fetch list of CDR filters
 */
export async function fetchCdrFilters(
  params: ListCdrFiltersParams,
  domainId?: number
): Promise<ListCdrFiltersResponse> {
  const queryParams = new URLSearchParams();

  if (params.page != null) queryParams.append('page', String(params.page));
  if (params.limit != null) queryParams.append('limit', String(params.limit));
  if (params.search) queryParams.append('search', params.search);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.enabled != null) queryParams.append('enabled', String(params.enabled));
  if (domainId != null) queryParams.append('domainId', String(domainId));

  const url = buildApiUrl(`${CDR_FILTERS_ENDPOINT}?${queryParams.toString()}`);
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch CDR filters' }));
    throw new Error(error.message || 'Failed to fetch CDR filters');
  }

  return response.json();
}

/**
 * Fetch single CDR filter by ID or UUID
 */
export async function fetchCdrFilter(id: string | number): Promise<GetCdrFilterResponse> {
  const url = buildApiUrl(`${CDR_FILTERS_ENDPOINT}/${id}`);
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch CDR filter' }));
    throw new Error(error.message || 'Failed to fetch CDR filter');
  }

  return response.json();
}

/**
 * Create new CDR filter
 */
export async function createCdrFilter(
  payload: CreateCdrFilterPayload
): Promise<GetCdrFilterResponse> {
  const url = buildApiUrl(CDR_FILTERS_ENDPOINT);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create CDR filter' }));
    throw new Error(error.message || 'Failed to create CDR filter');
  }

  return response.json();
}

/**
 * Update CDR filter
 */
export async function updateCdrFilter(
  id: string | number,
  payload: UpdateCdrFilterPayload
): Promise<GetCdrFilterResponse> {
  const url = buildApiUrl(`${CDR_FILTERS_ENDPOINT}/${id}`);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      ...getApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update CDR filter' }));
    throw new Error(error.message || 'Failed to update CDR filter');
  }

  return response.json();
}

/**
 * Delete CDR filter
 */
export async function deleteCdrFilter(id: string | number): Promise<void> {
  const url = buildApiUrl(`${CDR_FILTERS_ENDPOINT}/${id}`);
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete CDR filter' }));
    throw new Error(error.message || 'Failed to delete CDR filter');
  }
}

